import React, { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import DottedMap from 'dotted-map/without-countries';
import worldMapData from '../../acquirers/worldMapData.json';
import { REGION_FLAGS, resolveCountryGeo } from '../../acquirers/constants/constants';
import CountryFlag from '../../../components/shared/CountryFlag';
import { useTheme } from '../../../store/themeStore';
import { HEATMAP_TYPES } from '../../transaction-monitoring/constants/constants';
import type { OrderAnalytics, TerminalApprovalRate } from '../../transaction-monitoring/models/types';
import { resolveHeatmapType } from '../../transaction-monitoring/utils/utils';
import '../../transaction-monitoring/TransactionMonitoring.css';
import '../../acquirers/Acquirers.css';

type TransactionDashboardChartsProps = {
  analytics: OrderAnalytics;
  /** Gates the acquirer/terminal traffic donuts. The backend already withholds the data from
   *  everyone else, so this only avoids rendering two permanently-empty cards. */
  isSuperAdminUser?: boolean;
};

type FootprintPin = {
  x: number;
  y: number;
  country: string;
  region: string;
  weight: number;
};

type FootprintRegion = {
  name: string;
  countries: number;
  flag: string;
  countryList: Array<{
    country: string;
    weight: number;
  }>;
};

type TooltipPlacement = 'above' | 'below';

type SplitSlice = {
  name: string;
  value: number;
  color: string;
  percentage: number;
};

type ChartTooltipItem = {
  name?: unknown;
  value?: unknown;
  color?: unknown;
  dataKey?: unknown;
  payload?: { percentage?: number };
};

type ChartTooltipProps = {
  active?: boolean;
  payload?: ReadonlyArray<ChartTooltipItem>;
  label?: unknown;
};

const SPLIT_COLORS = ['#22c55e', '#8b5cf6', '#3b82f6', '#f97316', '#14b8a6', '#a78bfa', '#ef4444'];
/** Deliberately outside SPLIT_COLORS: the folded-tail slice should read as "not a real category". */
const OTHER_SLICE_COLOR = '#94a3b8';

const STATUS_BREAKDOWN = [
  { key: 'Completed', color: '#2dd4bf' },
  { key: 'Pending', color: '#fb923c' },
  { key: 'Awaiting', color: '#60a5fa' },
  { key: 'Failed', color: '#f43f5e' },
] as const;

const STATUS_SERIES = [
  { key: 'Successful', color: '#3b82f6' },
  { key: 'Pending', color: '#ffa502' },
  { key: 'Failed', color: '#ff4757' },
] as const;

function DonutSliceTooltip({
  active,
  payload,
  total,
  slices,
}: ChartTooltipProps & { total: number; slices: SplitSlice[] }) {
  if (!active || !payload?.length) return null;
  const activeName = String(payload[0].name ?? '');

  return (
    <div className="tm-recharts-tooltip">
      {slices.map((slice) => (
        <div
          className={`tm-recharts-tooltip-row${slice.name === activeName ? ' is-active' : ''}`}
          key={slice.name}
        >
          <span className="tm-chart-legend-dot" style={{ background: slice.color }} />
          <span>
            {slice.name}: {slice.value.toLocaleString()} ({Math.round(slice.percentage)}%)
          </span>
        </div>
      ))}
      <div className="tm-recharts-tooltip-row tm-recharts-tooltip-total">
        Total: {total.toLocaleString()}
      </div>
    </div>
  );
}

function StatusTrendTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;

  return (
    <div className="tm-recharts-tooltip">
      <div className="tm-recharts-tooltip-label">{String(label ?? '')}</div>
      {payload.map((item) => (
        <div className="tm-recharts-tooltip-row" key={String(item.dataKey ?? item.name)}>
          <span
            className="tm-chart-legend-dot"
            style={{ background: String(item.color ?? '') }}
          />
          <span>
            {String(item.name ?? '')}: {Number(item.value ?? 0).toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

function HourlyTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="tm-recharts-tooltip">
      {String(label ?? '')}: {Number(payload[0].value ?? 0).toLocaleString()}
    </div>
  );
}

/** Colour by health, not by identity: a super admin should be able to spot a bad terminal without
 *  reading any numbers. Thresholds are deliberately blunt — tune once you know your own baseline. */
function approvalColor(rate: number): string {
  if (rate >= 85) return '#22c55e';
  if (rate >= 70) return '#f97316';
  return '#ef4444';
}

type ApprovalDatum = {
  label: string;
  fullName: string;
  approvalRate: number;
  approved: number;
  total: number;
};

/** Shared by the bar plot and the pinned percent axis so the ticks stay aligned with the bars. */
const APPROVAL_Y_AXIS_WIDTH = 104;
const APPROVAL_AXIS_HEIGHT = 30;
/** Same row pitch and bar thickness in collapsed and expanded views. */
const APPROVAL_ROW_HEIGHT = 34;
const APPROVAL_MAX_BAR_SIZE = 18;
/** The pinned axis is a short chart. It needs a plot above the tick band or Recharts draws no ticks. */
const APPROVAL_AXIS_CHART_HEIGHT = 64;
const APPROVAL_PLOT_MARGIN = { top: 4, right: 28, left: 0, bottom: 0 } as const;
const APPROVAL_AXIS_MARGIN = { top: 0, right: 28, left: APPROVAL_Y_AXIS_WIDTH, bottom: 0 } as const;

function ApprovalTooltip({
  active,
  label,
  coordinate,
  rows,
  anchorRef,
}: ChartTooltipProps & {
  rows: ApprovalDatum[];
  coordinate?: { x?: number; y?: number };
  anchorRef: React.RefObject<HTMLDivElement | null>;
}) {
  const tipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const row = active ? rows.find((item) => item.label === String(label ?? '')) : undefined;
  const surface = anchorRef.current?.querySelector('.recharts-surface');
  const bounds = surface?.getBoundingClientRect();
  const anchorX = bounds && coordinate?.x != null ? bounds.left + coordinate.x : null;
  const anchorY = bounds && coordinate?.y != null ? bounds.top + coordinate.y : null;

  // The plot scrolls and clips overflow, so the tooltip is portaled out of the chart.
  // Place it beside the bar, then pull it back inside the viewport if the label is long.
  useLayoutEffect(() => {
    const node = tipRef.current;
    if (!node || anchorX == null || anchorY == null) return;
    const rect = node.getBoundingClientRect();
    const pad = 8;
    let left = anchorX + 12;
    let top = anchorY - rect.height / 2;
    if (left + rect.width > window.innerWidth - pad) left = window.innerWidth - pad - rect.width;
    if (left < pad) left = pad;
    if (top + rect.height > window.innerHeight - pad) top = window.innerHeight - pad - rect.height;
    if (top < pad) top = pad;
    setPos((previous) => (previous && previous.left === left && previous.top === top ? previous : { left, top }));
  }, [anchorX, anchorY, row]);

  if (!row || anchorX == null || anchorY == null) return null;

  return createPortal(
    <div
      ref={tipRef}
      className="tm-recharts-tooltip tm-approval-tooltip"
      style={{
        left: pos?.left ?? anchorX + 12,
        top: pos?.top ?? anchorY,
        transform: pos ? undefined : 'translateY(-50%)',
      }}
    >
      {row.fullName}: {row.approved.toLocaleString()}/{row.total.toLocaleString()} (
      {Math.round(row.approvalRate)}%)
    </div>,
    document.body,
  );
}

/**
 * Approval rate per terminal, as a horizontal bar chart.
 *
 * <p>Horizontal, not vertical: terminal names are long and would either overlap or need rotating
 * on a bottom axis. And a bar chart rather than a donut because approval rates are independent
 * percentages that do not sum to 100 — a pie of them would be meaningless.
 *
 * <p>Bars are coloured by health band rather than identity, so a bad terminal is visible without
 * reading a single number; the exact approved/total is in the tooltip.
 */
function ApprovalRateCard({
  data,
  tickColor,
  gridColor,
  isDark,
  collapseAfter = 9,
}: {
  data: TerminalApprovalRate[];
  tickColor: string;
  gridColor: string;
  isDark: boolean;
  collapseAfter?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const collapsible = data.length > collapseAfter;
  const plotRef = useRef<HTMLDivElement>(null);
  const [slotHeight, setSlotHeight] = useState(0);
  const [axisGutter, setAxisGutter] = useState(0);

  const rows = useMemo<ApprovalDatum[]>(() => {
    const visible = !collapsible || expanded ? data : data.slice(0, collapseAfter);
    // Axis categories double as the tooltip lookup key, so they must be unique. Two acquirers can
    // each have a terminal with the same name — disambiguate only those, to keep labels short.
    const nameCounts = new Map<string, number>();
    visible.forEach((item) => nameCounts.set(item.name, (nameCounts.get(item.name) ?? 0) + 1));
    return visible.map((item) => ({
      label: (nameCounts.get(item.name) ?? 0) > 1 && item.acquirerName
        ? `${item.name} · ${item.acquirerName}`
        : item.name,
      fullName: item.acquirerName ? `${item.name} (${item.acquirerName})` : item.name,
      approvalRate: item.approvalRate,
      approved: item.approved,
      total: item.total,
    }));
  }, [data, collapsible, expanded, collapseAfter]);

  // Measure the plot slot so collapsed bars stretch to the pinned x-axis; keep axis aligned with the grid.
  useLayoutEffect(() => {
    const node = plotRef.current;
    if (!node) return undefined;
    const update = () => {
      const next = node.clientHeight;
      setSlotHeight((previous) => (previous === next ? previous : next));
      if (!collapsible) {
        setAxisGutter(0);
        return;
      }
      const gutter = Math.max(0, node.offsetWidth - node.clientWidth);
      setAxisGutter((previous) => (previous === gutter ? previous : gutter));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [collapsible, rows.length, expanded]);

  const rowStackHeight = Math.max(APPROVAL_ROW_HEIGHT, rows.length * APPROVAL_ROW_HEIGHT);
  // Collapsed (or non-collapsible): grow the chart to the slot so bars reach the x-axis. Expanded: fixed row pitch + scroll.
  const chartHeight =
    collapsible && expanded ? rowStackHeight : Math.max(rowStackHeight, slotHeight);
  const barTrack = chartHeight / Math.max(rows.length, 1);
  const maxBarSize =
    collapsible && expanded
      ? APPROVAL_MAX_BAR_SIZE
      : Math.max(APPROVAL_MAX_BAR_SIZE, Math.min(36, Math.round(barTrack * 0.62)));

  return (
    <div className="tm-chart-card tm-split-card tm-approval-card">
      <div className="tm-chart-header tm-split-header">
        <div>
          <span className="tm-chart-title">Approval Rate</span>
          <div className="tm-chart-sub">Approved share by terminal, highest first</div>
        </div>
      </div>
      {rows.length === 0 ? (
        <ul className="tm-split-legend">
          <li className="tm-split-legend-item">
            <span className="tm-split-legend-name">No data</span>
            <span className="tm-split-legend-pct">—</span>
          </li>
        </ul>
      ) : (
        <div className="tm-recharts-wrap tm-approval-body">
          <div
            ref={plotRef}
            className={`tm-approval-plot${collapsible ? ' tm-approval-plot--scrollable' : ''}`}
          >
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart
                data={rows}
                layout="vertical"
                margin={APPROVAL_PLOT_MARGIN}
              >
                <CartesianGrid horizontal={false} stroke={gridColor} strokeDasharray="4 4" />
                <XAxis type="number" domain={[0, 100]} hide height={0} />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={APPROVAL_Y_AXIS_WIDTH}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: tickColor, fontSize: 11, fontWeight: 500 }}
                />
                <Tooltip
                  cursor={{ fill: isDark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.04)' }}
                  wrapperStyle={{ visibility: 'hidden' }}
                  content={({ active, label, coordinate }) => (
                    <ApprovalTooltip
                      active={active}
                      label={label}
                      coordinate={coordinate}
                      rows={rows}
                      anchorRef={plotRef}
                    />
                  )}
                />
                <Bar dataKey="approvalRate" radius={[0, 6, 6, 0]} maxBarSize={maxBarSize}>
                  {rows.map((row) => (
                    <Cell key={row.label} fill={approvalColor(row.approvalRate)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div
            className="tm-approval-xaxis"
            style={{ height: APPROVAL_AXIS_HEIGHT, paddingRight: axisGutter }}
          >
            <div
              style={{
                height: APPROVAL_AXIS_CHART_HEIGHT,
                marginTop: APPROVAL_AXIS_HEIGHT - APPROVAL_AXIS_CHART_HEIGHT,
              }}
            >
              <ResponsiveContainer width="100%" height={APPROVAL_AXIS_CHART_HEIGHT}>
                <BarChart data={[{ approvalRate: 0 }]} margin={APPROVAL_AXIS_MARGIN}>
                  <XAxis
                    type="number"
                    dataKey="approvalRate"
                    domain={[0, 100]}
                    height={APPROVAL_AXIS_HEIGHT}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: tickColor, fontSize: 11, fontWeight: 500 }}
                    tickFormatter={(value) => `${Math.round(Number(value))}%`}
                  />
                  <YAxis hide width={0} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
      {collapsible && (
        <button
          type="button"
          className="tm-split-legend-more"
          onClick={() => setExpanded((previous) => !previous)}
        >
          {expanded ? 'Show less' : `Show all ${data.length}`}
        </button>
      )}
    </div>
  );
}

function SplitDonutCard({
  title,
  subtitle,
  data,
  showShareBars = false,
  collapseAfter,
}: {
  title: string;
  subtitle: string;
  data: SplitSlice[];
  showShareBars?: boolean;
  /** Opt-in: show only this many rows, folding the rest into one "Other" slice with a Show all
   *  toggle. Omitted by Currency mix and Status Breakdown, whose lists are inherently short, so
   *  their behaviour is completely unchanged. */
  collapseAfter?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasData = data.some((item) => item.value > 0);
  // Always the full total, regardless of what the legend is currently showing - the number in the
  // middle of the donut must not change when someone expands or collapses the list.
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const collapsible = typeof collapseAfter === 'number' && data.length > collapseAfter;

  // Drives BOTH the donut and the legend, so a slice's colour means the same thing in each, and
  // expanding can never leave a legend row pointing at a wedge that isn't drawn.
  const visible = useMemo(() => {
    if (!collapsible || expanded) return data;
    const head = data.slice(0, collapseAfter);
    const tail = data.slice(collapseAfter);
    return [
      ...head,
      {
        name: `Other (${tail.length})`,
        value: tail.reduce((sum, item) => sum + item.value, 0),
        color: OTHER_SLICE_COLOR,
        percentage: tail.reduce((sum, item) => sum + item.percentage, 0),
      },
    ];
  }, [data, collapsible, expanded, collapseAfter]);

  const chartData = hasData
    ? visible
    : [{ name: 'No data', value: 1, color: '#cbd5e1', percentage: 0 }];

  return (
    <div className="tm-chart-card tm-split-card">
      <div className="tm-chart-header tm-split-header">
        <div>
          <span className="tm-chart-title">{title}</span>
          <div className="tm-chart-sub">{subtitle}</div>
        </div>
      </div>
      <div className="tm-split-donut tm-recharts-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="68%"
              outerRadius="88%"
              stroke="none"
              paddingAngle={hasData && data.length > 1 ? 3 : 0}
            >
              {chartData.map((slice) => (
                <Cell key={slice.name} fill={slice.color} />
              ))}
            </Pie>
            {hasData && (
              <Tooltip
                cursor={false}
                wrapperStyle={{ outline: 'none', zIndex: 20, pointerEvents: 'none' }}
                content={({ active, payload }) => (
                  <DonutSliceTooltip
                    active={active}
                    payload={payload}
                    total={total}
                    slices={visible}
                  />
                )}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
        <div className="tm-split-donut-total" aria-hidden={!hasData}>
          <span className="tm-split-donut-total-label">Total</span>
          <span className="tm-split-donut-total-value">
            {hasData ? total.toLocaleString() : '—'}
          </span>
        </div>
      </div>
      <ul className={`tm-split-legend${expanded ? ' tm-split-legend--scroll' : ''}`}>
        {data.length === 0 ? (
          <li className="tm-split-legend-item">
            <span className="tm-split-legend-name">No data</span>
            <span className="tm-split-legend-pct">—</span>
          </li>
        ) : (
          visible.map((item) => (
            <li className="tm-split-legend-item" key={item.name}>
              <span className="tm-split-legend-name">
                <span className="tm-split-legend-dot" style={{ background: item.color }} />
                {item.name}
              </span>
              <span className="tm-split-legend-pct">
                {showShareBars && (
                  <span
                    className="tm-split-legend-bar"
                    style={{
                      background: item.color,
                      width: `${Math.max(8, (item.percentage / 100) * 48)}px`,
                    }}
                  />
                )}
                <span className="tm-split-legend-value">{item.value.toLocaleString()}</span>
                {Math.round(item.percentage)}%
              </span>
            </li>
          ))
        )}
      </ul>
      {collapsible && (
        <button
          type="button"
          className="tm-split-legend-more"
          onClick={() => setExpanded((previous) => !previous)}
        >
          {expanded ? 'Show less' : `Show all ${data.length}`}
        </button>
      )}
    </div>
  );
}

function parseHourLabel(label: string): number | null {
  const match = /^(\d{1,2})/.exec(label.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  return Number.isFinite(hour) && hour >= 0 && hour < 24 ? hour : null;
}

function getMapPinTooltipPosition(
  svg: SVGSVGElement,
  wrap: HTMLDivElement,
  pinX: number,
  pinY: number,
): { left: number; top: number; placement: TooltipPlacement } {
  const point = svg.createSVGPoint();
  point.x = pinX;
  point.y = pinY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return { left: 0, top: 0, placement: 'above' };

  const transformed = point.matrixTransform(ctm);
  const wrapRect = wrap.getBoundingClientRect();
  const left = transformed.x - wrapRect.left;
  const top = transformed.y - wrapRect.top;
  const edgePad = 96;
  const clampedLeft = Math.min(Math.max(left, edgePad), Math.max(edgePad, wrapRect.width - edgePad));
  // Flip below when the pin is too close to the top edge so the tooltip isn't clipped.
  const placement: TooltipPlacement = top < 88 ? 'below' : 'above';

  return { left: clampedLeft, top, placement };
}

export default function TransactionDashboardCharts({
  analytics,
  isSuperAdminUser = false,
}: TransactionDashboardChartsProps) {
  const { theme } = useTheme();
  const mapWrapRef = useRef<HTMLDivElement>(null);
  const mapSvgRef = useRef<SVGSVGElement>(null);
  const [hoveredPin, setHoveredPin] = useState<FootprintPin | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{
    left: number;
    top: number;
    placement: TooltipPlacement;
  } | null>(null);
  const gradientId = useId().replace(/:/g, '');
  const isDark = theme === 'dark';
  const tickColor = isDark ? '#94a3b8' : '#475569';
  const gridColor = isDark ? 'rgba(148,163,184,0.08)' : 'rgba(0,0,0,0.06)';

  const totalPendingTransactions = analytics.totalPendingTransactions;
  const totalRefundTransactions = analytics.totalRefundTransactions;

  const heatmapData = useMemo(() => {
    const grid: Record<(typeof HEATMAP_TYPES)[number], number[]> = {
      Card: Array(24).fill(0),
      Transfer: Array(24).fill(0),
      Crypto: Array(24).fill(0),
    };

    const hourly = Array(24).fill(0) as number[];
    Object.entries(analytics.hourlyTrend).forEach(([label, count]) => {
      const hour = parseHourLabel(label);
      if (hour == null) return;
      hourly[hour] = Number(count) || 0;
    });

    // hourlyTrend is aggregate; bucket into Card/Transfer/Crypto using paymentTypes share.
    const paymentTypes =
      analytics.paymentTypes.length > 0
        ? analytics.paymentTypes
        : [{ paymentMethod: 'CREDIT_CARD', percentage: 100 }];
    const totalPct =
      paymentTypes.reduce((sum, item) => sum + Math.max(0, Number(item.percentage) || 0), 0) || 100;

    paymentTypes.forEach((item) => {
      const row = resolveHeatmapType(item.paymentMethod);
      const share = Math.max(0, Number(item.percentage) || 0) / totalPct;
      hourly.forEach((count, hour) => {
        grid[row][hour] += count * share;
      });
    });

    HEATMAP_TYPES.forEach((type) => {
      grid[type] = grid[type].map((value) => Math.round(value));
    });

    const max = Math.max(1, ...Object.values(grid).flat());
    return { grid, max };
  }, [analytics.hourlyTrend, analytics.paymentTypes]);

  const footprint = useMemo(() => {
    const mapMeta = worldMapData as unknown as {
      width: number;
      height: number;
    };

    const byCountry = new Map<string, number>();
    Object.entries(analytics.countryTransactions).forEach(([country, count]) => {
      const name = country.trim();
      if (!name || name === '—' || name === '-' || name === 'Unknown') return;
      const weight = Number(count) || 0;
      if (weight <= 0) return;
      byCountry.set(name, (byCountry.get(name) || 0) + weight);
    });

    const byRegion = new Map<string, Set<string>>();
    byCountry.forEach((_weight, country) => {
      const geo = resolveCountryGeo(country);
      if (!geo) return;
      if (!byRegion.has(geo.region)) byRegion.set(geo.region, new Set());
      byRegion.get(geo.region)!.add(country);
    });

    const regions: FootprintRegion[] = Array.from(byRegion.entries())
      .map(([name, countryNames]) => {
        const countryList = Array.from(countryNames)
          .map((country) => ({
            country,
            weight: byCountry.get(country)!,
          }))
          .sort((a, b) => b.weight - a.weight);

        return {
          name,
          countries: countryList.length,
          flag: REGION_FLAGS[name] || '🌍',
          countryList,
        };
      })
      .sort((a, b) => b.countries - a.countries);

    const map = new DottedMap({ map: worldMapData as any });
    const pins: FootprintPin[] = [];

    byCountry.forEach((weight, country) => {
      const geo = resolveCountryGeo(country);
      if (!geo) return;
      const point = map.addPin({
        lat: geo.lat,
        lng: geo.lng,
        svgOptions: { color: '#29D4FF', radius: 0.7 },
      });
      pins.push({
        x: point.x,
        y: point.y,
        country,
        region: geo.region,
        weight,
      });
    });

    const svg = map.getSVG({
      radius: 0.24,
      color: 'rgba(104,115,128,0.35)',
      shape: 'circle',
      backgroundColor: 'transparent',
    });

    const hub = pins.slice().sort((a, b) => b.weight - a.weight)[0];
    const topPerRegion = new Map<string, (typeof pins)[number]>();
    pins.forEach((p) => {
      const current = topPerRegion.get(p.region);
      if (!current || p.weight > current.weight) topPerRegion.set(p.region, p);
    });

    const routes: Array<[(typeof pins)[number], (typeof pins)[number]]> = [];
    if (hub) {
      topPerRegion.forEach((p) => {
        if (p.country !== hub.country) routes.push([hub, p]);
      });
    }

    return {
      svg,
      width: mapMeta.width,
      height: mapMeta.height,
      pins,
      routes,
      regions,
    };
  }, [analytics.countryTransactions]);

  const updateTooltipPos = useCallback((pin: FootprintPin | null) => {
    if (!pin || !mapWrapRef.current || !mapSvgRef.current) {
      setTooltipPos(null);
      return;
    }
    setTooltipPos(
      getMapPinTooltipPosition(mapSvgRef.current, mapWrapRef.current, pin.x, pin.y),
    );
  }, []);

  useLayoutEffect(() => {
    updateTooltipPos(hoveredPin);
  }, [hoveredPin, footprint.width, footprint.height, updateTooltipPos]);

  useLayoutEffect(() => {
    const wrap = mapWrapRef.current;
    if (!wrap) return undefined;

    const observer = new ResizeObserver(() => updateTooltipPos(hoveredPin));
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [hoveredPin, updateTooltipPos]);

  const statusChartData = useMemo(
    () =>
      analytics.volumeTrend.map((day) => ({
        label: day.label,
        Successful: day.completedCount,
        Pending: day.pendingCount,
        Failed: day.failedCount,
      })),
    [analytics.volumeTrend],
  );

  const currencySlices = useMemo<SplitSlice[]>(
    () =>
      analytics.topCurrencies.map((item, index) => ({
        name: item.currency,
        value: item.amount,
        color: SPLIT_COLORS[index % SPLIT_COLORS.length],
        percentage: item.percentage,
      })),
    [analytics.topCurrencies],
  );

  // Acquirer/terminal traffic slices. Colour comes from the shared SPLIT_COLORS ramp by index,
  // exactly as currencySlices does, so these donuts sit visually alongside the existing two.
  // percentage is computed server-side (one decimal) — don't recompute it from count here, or a
  // rounding difference would make the legend disagree with the backend's own numbers.
  const acquirerSlices = useMemo<SplitSlice[]>(
    () =>
      analytics.acquirerSplit.map((item, index) => ({
        name: item.name,
        value: item.count,
        color: SPLIT_COLORS[index % SPLIT_COLORS.length],
        percentage: item.percentage,
      })),
    [analytics.acquirerSplit],
  );

  // Terminal names are only unique within an acquirer, so label them "Terminal (Acquirer)" —
  // otherwise two acquirers' identically-named terminals collapse into one indistinguishable
  // legend entry (and React would see duplicate keys on the Cell list).
  const merchantSlices = useMemo<SplitSlice[]>(
    () =>
      analytics.merchantSplit.map((item, index) => ({
        name: item.name,
        value: item.amount,
        color: SPLIT_COLORS[index % SPLIT_COLORS.length],
        percentage: item.percentage,
      })),
    [analytics.merchantSplit],
  );

  const statusSlices = useMemo<SplitSlice[]>(() => {
    const items = [
      {
        name: STATUS_BREAKDOWN[0].key,
        value: analytics.totalCompletedTransactions,
        color: STATUS_BREAKDOWN[0].color,
      },
      {
        name: STATUS_BREAKDOWN[1].key,
        value: totalPendingTransactions,
        color: STATUS_BREAKDOWN[1].color,
      },
      {
        name: STATUS_BREAKDOWN[2].key,
        value: analytics.totalAwaitingCheckoutTransactions,
        color: STATUS_BREAKDOWN[2].color,
      },
      {
        name: STATUS_BREAKDOWN[3].key,
        value: analytics.totalFailedTransactions,
        color: STATUS_BREAKDOWN[3].color,
      },
    ];
    const total = items.reduce((sum, item) => sum + item.value, 0);
    return items.map((item) => ({
      ...item,
      percentage: total > 0 ? (item.value / total) * 100 : 0,
    }));
  }, [
    analytics.totalAwaitingCheckoutTransactions,
    analytics.totalCompletedTransactions,
    analytics.totalFailedTransactions,
    totalPendingTransactions,
  ]);

  const hourlyChartData = useMemo(() => {
    const hourly = Array(24).fill(0) as number[];
    Object.entries(analytics.hourlyTrend).forEach(([label, count]) => {
      const hour = parseHourLabel(label);
      if (hour == null) return;
      hourly[hour] = Number(count) || 0;
    });

    return Array.from({ length: 12 }, (_, index) => {
      const hour = index * 2;
      return {
        label: `${String(hour).padStart(2, '0')}h`,
        count: hourly[hour] + hourly[hour + 1],
      };
    });
  }, [analytics.hourlyTrend]);

  const kpiCards = useMemo(() => {
    const completed = analytics.totalCompletedTransactions;
    const failed = analytics.totalFailedTransactions;
    const pending = totalPendingTransactions;
    const refunded = totalRefundTransactions;
    const statusTotal = completed + failed + pending + refunded;
    const share = (count: number) =>
      statusTotal > 0 ? `${((count / statusTotal) * 100).toFixed(1)}%` : '0%';

    return [
      {
        label: 'Completed',
        value: completed.toLocaleString(),
        badge: share(completed),
        badgeType: 'up' as const,
        color: '#22c55e',
      },
      {
        label: 'Failed',
        value: failed.toLocaleString(),
        badge: share(failed),
        badgeType: 'down' as const,
        color: '#ef4444',
      },
      {
        label: 'Pending',
        value: pending.toLocaleString(),
        badge: share(pending),
        badgeType: 'neutral' as const,
        color: '#ffa502',
      },
      {
        label: 'Refunded',
        value: refunded.toLocaleString(),
        badge: share(refunded),
        badgeType: 'neutral' as const,
        color: '#7F77DD',
      },
    ];
  }, [
    analytics.totalCompletedTransactions,
    analytics.totalFailedTransactions,
    totalPendingTransactions,
    totalRefundTransactions,
  ]);

  return (
    <>
      <div className="tm-kpi-grid">
        {kpiCards.map((card) => (
          <div
            className="tm-kpi-card"
            key={card.label}
            style={{ '--kpi-color': card.color } as React.CSSProperties}
          >
            <div className="tm-kpi-top">
              <span className="tm-kpi-label">{card.label}</span>
              <span className={`tm-kpi-badge ${card.badgeType}`}>{card.badge}</span>
            </div>
            <div className="tm-kpi-value">{card.value}</div>
            <div className="tm-kpi-rule" />
          </div>
        ))}
      </div>

      <div className="tm-charts-grid">
        <SplitDonutCard
          title="Currency mix"
          subtitle="Volume share by currency"
          data={currencySlices}
        />
        <SplitDonutCard
          title="Status Breakdown"
          subtitle="Transaction outcomes"
          data={statusSlices}
          showShareBars
        />

        <div className="tm-chart-card tm-split-card">
          <div className="tm-chart-header tm-split-header">
            <div>
              <span className="tm-chart-title">Hourly Distribution</span>
              <div className="tm-chart-sub">Transactions by hour of day</div>
            </div>
          </div>
          <div className="tm-hourly-body tm-recharts-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyChartData} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={gridColor} strokeDasharray="4 4" />
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: tickColor, fontSize: 11, fontWeight: 500 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  width={36}
                  allowDecimals={false}
                  tick={{ fill: tickColor, fontSize: 11, fontWeight: 500 }}
                  tickFormatter={(value) => Math.round(Number(value)).toLocaleString()}
                />
                <Tooltip
                  cursor={{ fill: isDark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.04)' }}
                  content={({ active, payload, label }) => (
                    <HourlyTooltip active={active} payload={payload} label={label} />
                  )}
                />
                <Bar dataKey="count" fill="#4f6bff" radius={[6, 6, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Own row, reusing .tm-charts-grid so these align to the same 3-column track (and inherit
          its responsive rules) as the cards above. Deliberately NOT appended to that first grid:
          a 5th card there wraps into a second row shared with Hourly Distribution, whose
          min-height:240px then stretches the donut beside it via align-items:stretch. */}
      {isSuperAdminUser && (
        <div className="tm-charts-grid">
          <SplitDonutCard
            title="Acquirer Split"
            subtitle="Share of transactions by acquirer"
            data={acquirerSlices}
            collapseAfter={6}
          />
          <SplitDonutCard
            title="Merchant Split"
            subtitle="Share of amount processed by merchant"
            data={merchantSlices}
            collapseAfter={6}
          />
          <ApprovalRateCard
            data={analytics.terminalApprovalRates}
            tickColor={tickColor}
            gridColor={gridColor}
            isDark={isDark}
          />
        </div>
      )}

      <div className="tm-chart-card tm-trend-card">
        <div className="tm-chart-header tm-split-header">
          <div>
            <span className="tm-chart-title">Transactions (7d)</span>
            <div className="tm-chart-sub">Status over the last 7 days</div>
            <div className="tm-chart-legend">
              {STATUS_SERIES.map((series) => (
                <span className="tm-chart-legend-item" key={series.key}>
                  <span className="tm-chart-legend-dot" style={{ background: series.color }} />
                  {series.key}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="tm-chart-body tm-recharts-wrap">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={statusChartData}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <defs>
                {STATUS_SERIES.map((series) => (
                  <linearGradient
                    id={`tmStatus${series.key}${gradientId}`}
                    key={series.key}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={series.color} stopOpacity={0.35} />
                    <stop offset="90%" stopColor={series.color} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid vertical={false} stroke={gridColor} strokeDasharray="4 4" />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fill: tickColor, fontSize: 11, fontWeight: 500 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={40}
                allowDecimals={false}
                tick={{ fill: tickColor, fontSize: 11, fontWeight: 500 }}
                tickFormatter={(value) => Math.round(Number(value)).toLocaleString()}
              />
              <Tooltip
                cursor={{ stroke: gridColor }}
                content={({ active, payload, label }) => (
                  <StatusTrendTooltip active={active} payload={payload} label={label} />
                )}
              />
              {STATUS_SERIES.map((series) => (
                <Area
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  stroke={series.color}
                  strokeWidth={2.5}
                  fill={`url(#tmStatus${series.key}${gradientId})`}
                  fillOpacity={1}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="acq-map-row">
        <div className="dh-chart-card acq-map-card">
          <div className="tm-chart-header">
            <div>
              <span className="tm-chart-title">Global transaction footprint</span>
              <div className="tm-heatmap-sub">Geographic coverage</div>
            </div>
            <span className="tm-chart-period">
              <span className="dh-dot" style={{ background: '#3b82f6' }} /> Routes
            </span>
          </div>

          <div
            ref={mapWrapRef}
            className="acq-map-svg-wrap"
            onMouseLeave={() => setHoveredPin(null)}
          >
            <div className="acq-map-svg-clip">
              <img
                className="acq-map-bg"
                src={`data:image/svg+xml;utf8,${encodeURIComponent(footprint.svg)}`}
                alt=""
              />
              <svg
                ref={mapSvgRef}
                className="acq-map-svg"
                viewBox={`0 0 ${footprint.width} ${footprint.height}`}
                preserveAspectRatio="xMidYMid meet"
              >
                <defs>
                  <radialGradient id="tmNodeGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#29D4FF" stopOpacity="0.65" />
                    <stop offset="100%" stopColor="#29D4FF" stopOpacity="0" />
                  </radialGradient>
                </defs>

                {footprint.routes.map(([from, to], i) => {
                  const mx = (from.x + to.x) / 2;
                  const my = Math.min(from.y, to.y) - footprint.height * 0.16;
                  return (
                    <path
                      key={`r-${i}`}
                      d={`M ${from.x} ${from.y} Q ${mx} ${my} ${to.x} ${to.y}`}
                      className="acq-map-route"
                    />
                  );
                })}
                {footprint.pins.map((n) => {
                  const isActive = hoveredPin?.country === n.country;
                  return (
                    <g key={n.country} className={isActive ? 'acq-map-pin-active' : undefined}>
                      <circle
                        cx={n.x}
                        cy={n.y}
                        r={2.4}
                        className="acq-map-pin-hit"
                        onMouseEnter={() => setHoveredPin(n)}
                        onMouseLeave={(event) => {
                          const related = event.relatedTarget;
                          if (
                            related instanceof Element &&
                            related.classList.contains('acq-map-pin-hit')
                          ) {
                            return;
                          }
                          setHoveredPin(null);
                        }}
                      />
                      <circle
                        cx={n.x}
                        cy={n.y}
                        r={isActive ? 2.2 : 1.6}
                        fill="url(#tmNodeGlow)"
                        pointerEvents="none"
                      />
                      <circle
                        cx={n.x}
                        cy={n.y}
                        r={isActive ? 0.75 : 0.55}
                        className="acq-map-node"
                        pointerEvents="none"
                      />
                    </g>
                  );
                })}
              </svg>
            </div>

            {hoveredPin && tooltipPos && (
              <div
                className={`acq-map-pin-tooltip${tooltipPos.placement === 'below' ? ' acq-map-pin-tooltip--below' : ''
                  }`}
                style={{
                  left: tooltipPos.left,
                  top: tooltipPos.top,
                }}
              >
                <div className="acq-map-pin-tooltip-flag">
                  <CountryFlag
                    countryName={hoveredPin.country}
                    className="acq-map-pin-tooltip-flag-img"
                  />
                </div>
                <div className="acq-map-pin-tooltip-body">
                  <span className="acq-map-pin-tooltip-country">{hoveredPin.country}</span>
                  <span className="acq-map-pin-tooltip-region">{hoveredPin.region}</span>
                  <span className="acq-map-pin-tooltip-count">
                    {hoveredPin.weight}{' '}
                    {hoveredPin.weight === 1 ? 'transaction' : 'transactions'}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="acq-region-row">
            {footprint.regions.length === 0 ? (
              <span className="acq-region-empty">No country data available yet</span>
            ) : (
              footprint.regions.map((r) => (
                <div key={r.name} className="acq-region-chip">
                  <div>
                    <span className="acq-region-eyebrow">{r.name.toUpperCase()}</span>
                    <span className="acq-region-value">
                      {r.countries} {r.countries === 1 ? 'country' : 'countries'}
                    </span>
                  </div>
                  <span className="acq-region-badge">{r.flag}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="tm-heatmap-card">
        <div className="tm-chart-header">
          <div>
            <span className="tm-chart-title">Global Transaction Heatmap</span>
            <div className="tm-heatmap-sub">Activity by hour last 24h</div>
          </div>
          <div className="tm-heatmap-legend">
            <span>Low</span>
            <div className="tm-heatmap-legend-bar" />
            <span>High</span>
          </div>
        </div>
        <div className="tm-heatmap-grid">
          <div />
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="tm-heatmap-hour-label" title={`${String(h).padStart(2, '0')}:00`}>
              {String(h).padStart(2, '0')}
            </div>
          ))}
          {HEATMAP_TYPES.map((type) => (
            <React.Fragment key={type}>
              <div className="tm-heatmap-row-label">{type}</div>
              {heatmapData.grid[type].map((count, hour) => {
                const intensity = count / heatmapData.max;
                return (
                  <div
                    key={`${type}-${hour}`}
                    className={`tm-heatmap-cell${count > 0 ? ' tm-heatmap-cell--active' : ''}`}
                    style={{
                      background: `rgba(59, 130, 246, ${0.08 + intensity * 0.88})`,
                    }}
                  >
                    {count > 0 ? count : null}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  );
}
